import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { PropsWithChildren, ReactNode, useState } from 'react';
import { Button } from 'react-bootstrap';
import { render } from 'react-dom';
import { ProgressPromise } from 'src/progress-promise';

import { WarningColour } from '../styles';
import AlertMessage from './alert-message';
import Modal, { Props } from './modal';

const WarningIcon = <FontAwesomeIcon icon={faExclamationTriangle} color={WarningColour} />;

export interface ShowModalProps<Result> extends Pick<Props, Exclude<keyof Props, 'isOpen' | 'buttons'>> {
  icon?: ReactNode | false;
  /**
   * `true` => close the modal<br>
   * `false` => don't close the modal<br>
   * `string` => wait for the returned progress ID before closing the modal
   */
  onBeforeClose?: (result: Result) => Promise<boolean | ProgressPromise> | ProgressPromise;
  buttons: (close: (result: Result) => void) => ReactNode;
  closeResult: Result;
  showWarning?: boolean;
  limit?: string;
}

export interface StandaloneModalProps<Result> extends ShowModalProps<Result> {
  onClose: (result: Result) => void;
}

function StandaloneModal<Result>(props: PropsWithChildren<StandaloneModalProps<Result>>): JSX.Element {
  const [show, setShow] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState<string>();
  const { buttons, children, closeResult, icon, onBeforeClose, onClose, showWarning, limit } = props;
  const close = async (result: Result) => {
    let shouldClose = true;
    if (onBeforeClose) {
      setLoading(true);
      setError(undefined);
      try {
        const val = await onBeforeClose(result);
        if (val === true || val === false) {
          shouldClose = val;
        } else {
          await val(setLoadingText);
        }
      } catch (e) {
        setError((e as Error).message ?? e);
        shouldClose = false;
      }
      setLoading(false);
    }
    if (shouldClose) {
      setShow(false);
      onClose?.(result);
    }
  };

  const content = icon ? (
    !showWarning ? (
      <div style={{ display: 'flex', gap: '0.25em' }}>
        <div style={{ flex: '0 0 auto', fontSize: '3em', margin: '0 0.25em' }}>{icon}</div>
        <div style={{ flexGrow: 1 }}>{children}</div>
      </div>
    ) : (
      <>
        <div style={{ display: 'flex', gap: '0.25em' }}>
          <div style={{ flex: '0 0 auto', fontSize: '3em', margin: '0 0.25em' }}>{icon}</div>
          <div style={{ flexGrow: 1 }}>{children}</div>
        </div>
        <div className="alert alert-warning no-margin" style={{ marginTop: '17px' }} id="HistoryLimitReachedWarning">
          {UiText.t('FilingImportNewDocument_MaxHistoryReachedWarning_Text').replace('{0}', limit ? limit : '0')}
        </div>
      </>
    )
  ) : (
    children
  );
  return (
    <Modal
      {...props}
      buttons={buttons((result) => close(result))}
      validationDisplay={error && <AlertMessage variant="danger" message={error} />}
      loading={loading}
      loadingText={loadingText}
      isOpen={show}
      onRequestClose={() => close(closeResult)}
      style={{ overlay: { zIndex: 600 }, content: { zIndex: 610 }, ...props.style }}
    >
      {content}
    </Modal>
  );
}

export function buttons<Result>(
  ...buttons: { content: ReactNode; isDefault?: boolean; result: Result; buttonIndex?: number }[]
): (close: (result: Result, buttonIndex: number | undefined) => void) => ReactNode {
  return (close) =>
    buttons.map((button, index) => (
      <Button
        key={index}
        size="sm"
        variant={button.isDefault ? 'primary' : 'secondary'}
        onClick={() => close(button.result, button.buttonIndex)}
      >
        {button.content}
      </Button>
    ));
}

/** In case you need a different result to showAlert or showConfirm. But you almost certainly want showAlert or showConfirm. */
export async function showModal<Result>(content: ReactNode, props: ShowModalProps<Result>): Promise<Result> {
  return new Promise((resolve) => {
    const container = document.body.appendChild(document.createElement('div'));
    const modal = (
      <StandaloneModal
        {...props}
        onClose={(result) => {
          container.remove();
          resolve(result);
        }}
      >
        {content}
      </StandaloneModal>
    );
    render(modal, container);
  });
}

export async function showAlert(
  title: string,
  content: ReactNode,
  props?: Partial<ShowModalProps<void> & { okButtonText: string }>,
): Promise<void> {
  return showModal(content, {
    buttons: buttons({ content: props?.okButtonText || UiText.t('Shared_OK_Button'), isDefault: true, result: undefined }),
    closeResult: undefined,
    height: 300,
    title,
    width: 500,
    ...props,
  });
}

export type ShowConfirmProps = Pick<StandaloneModalProps<boolean>, Exclude<keyof StandaloneModalProps<boolean>, 'onBeforeClose'>> & {
  onConfirm(): Promise<void | ProgressPromise> | ProgressPromise;
  okButtonText: string;
  cancelButtonText: string;
  showWarning?: boolean;
  limit?: string;
};

export async function showConfirm(title: string, content: ReactNode, props?: Partial<ShowConfirmProps>): Promise<boolean> {
  return showModal(content, {
    buttons: buttons(
      { content: props?.okButtonText || UiText.t('Shared_OK_Button'), isDefault: true, result: true },
      { content: props?.cancelButtonText || UiText.t('Shared_Cancel_Button'), result: false },
    ),
    closeResult: false,
    height: 250,
    icon: props?.icon ?? WarningIcon,
    onBeforeClose: async (result) => (result && (await props?.onConfirm?.())) || true,
    title,
    width: 500,
    showWarning: props?.showWarning,
    limit: props?.limit,
    ...props,
  });
}

export type ShowMultipleConfirmProps = Pick<StandaloneModalProps<number>, Exclude<keyof StandaloneModalProps<number>, 'onBeforeClose'>> & {
  onConfirm: (() => Promise<void | ProgressPromise> | ProgressPromise)[];
  okButtonText: string[];
  cancelButtonText: string;
};

export async function showMultipleConfirm(title: string, content: ReactNode, props?: Partial<ShowMultipleConfirmProps>): Promise<number> {
  const buttonContent: { content: string; isDefault: boolean; result: number }[] =
    props?.okButtonText?.map((buttonText, index) => ({
      content: buttonText,
      isDefault: true,
      result: index + 1,
    })) ?? [];
  buttonContent.push({
    content: props?.cancelButtonText || UiText.t('Shared_Cancel_Button'),
    isDefault: false,
    result: 0,
  });
  return showModal(content, {
    buttons: buttons(...buttonContent),
    closeResult: 0,
    height: 250,
    icon: props?.icon ?? WarningIcon,
    onBeforeClose: async (result) => (result ? (await props?.onConfirm?.[result - 1]?.()) || true : true),
    title,
    width: 500,
    ...props,
  });
}
